import { CalendarColorsSettings } from '../components/schedule/calendar-colors-settings.tsx';
import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { authApi, referralsApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { type PlanFeature } from '@shared/constants/index.ts';
import { companyHasPlanFeature } from '../components/plan-gate.tsx';
import { Alert, Button, LoadingBlock, PageHeader, TextInput } from '../components/ui.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import TeamSection from '../components/team-section.tsx';
import SavedTeamsSection from '../components/saved-teams-section.tsx';
import ShiftSwapPolicyCard from '../components/settings/shift-swap-policy-card.tsx';
import TeamPaySection from '../components/settings/team-pay-section.tsx';
import MarginFloorSection from '../components/settings/margin-floor-section.tsx';
import ApiKeysSection from '../components/api-keys-section.tsx';
import AiAssistantsSection from '../components/ai-assistants-section.tsx';
import SecuritySection from '../components/security-section.tsx';
import ReferralsSection from '../components/referrals-section.tsx';
import ApiDocsSection from '../components/api-docs-section.tsx';
import BillingSection from '../components/billing-section.tsx';
import LocationsSection from '../components/locations-section.tsx';
import CalendarFeedsSection from '../components/schedule/feeds-section.tsx';
import PaymentSettingsSection from '../components/payments/payment-settings-section.tsx';
import PaymentMethodsSection from '../components/payments/payment-methods-section.tsx';
import AiSection from '../components/ai-section.tsx';
import ConnectedAccountsSection from '../components/connected-accounts-section.tsx';
import IntegrationsSection from '../components/integrations-section.tsx';
import { DemoLimitedSection } from '../components/demo-limit-note.tsx';
import ProfileSection from '../components/profile-section.tsx';
import DeleteAccountSection from '../components/delete-account-section.tsx';
import { FounderContactLine } from '../components/founder-contact.tsx';
import { WidgetSection } from '../components/widget-section.tsx';
import PhotoQuotesCard from '../components/settings/photo-quotes-card.tsx';
import ServiceTemplatesSection from '../components/service-templates-section.tsx';
import RoomTemplatesSection from '../components/room-templates-section.tsx';
import GroupTemplatesSection from '../components/group-templates-section.tsx';
import ChecklistLibraryCard from '../components/settings/checklist-library-card.tsx';
import SetupGuide from '../components/settings/setup-guide.tsx';
import TipsCard from '../components/settings/tips-card.tsx';
import CompanyDetailsCard from '../components/settings/company-details-card.tsx';
import ExchangeRatesCard from '../components/settings/exchange-rates-card.tsx';
import RecurringHorizonCard from '../components/settings/recurring-horizon-card.tsx';
import WorkingHoursCard from '../components/settings/working-hours-card.tsx';
import RemindersCard from '../components/settings/reminders-card.tsx';
import ChecklistCompletionCard from '../components/settings/checklist-completion-card.tsx';
import LateCancelCard from '../components/settings/late-cancel-card.tsx';
import FillSlotsCard from '../components/settings/fill-slots-card.tsx';
import FollowUpsCard from '../components/settings/follow-ups-card.tsx';
import ReengagementCard from '../components/settings/reengagement-card.tsx';
import NotificationsCard from '../components/settings/notifications-card.tsx';
import TomorrowDigestCard from '../components/settings/tomorrow-digest-card.tsx';
import { useJobChatSettingsAccess } from '../components/settings/job-chat-access.ts';
import {
  settingsRouteForHash,
  settingsSectionRoute,
  useGoToSettingsSection,
  useSettingsHashJump,
  SettingsNav,
  SettingsNavIcon,
  type SettingsNavGroup,
} from '../components/settings-nav.tsx';
import { SETTINGS_HUB_HASH_ROUTES, SETTINGS_HUB_SECTION_ROUTES, settingsHubRoutesOn } from './settings/routes.ts';
import SettingsHub from '../components/settings/settings-hub.tsx';
import { settingsHubRedirect } from '../components/settings/settings-routes.ts';

/**
 * Everyday essentials come first. Specialist cards stay mounted inside an
 * Advanced disclosure so collapsing it never discards an unfinished form.
 * Existing permissions, plan gates and section anchors remain authoritative.
 * Pricing & quotes has grown into pages of its own under /settings/pricing,
 * and texting setup, calls and job chat into pages under /settings/messaging;
 * their entries here link there, and their old hashes redirect.
 *
 * While FEATURE_SETTINGS_HUB_ROUTES is on, more sections are pages of their
 * own (pages/settings/routes.ts): their cards are not stacked here, their
 * nav entries link there, and their old `#settings-<id>` links forward.
 */

/** Anchor target for the nav: focusable so a jump also moves the keyboard. */
function SettingsAnchor({ id, children }: { id: string; children: ReactNode }) {
  return (
    <div id={`settings-${id}`} tabIndex={-1} className="scroll-mt-4 outline-none">
      {children}
    </div>
  );
}

/**
 * Settings, in whichever shape this company has.
 *
 * With the whole-Settings hub on (`settingsHub`, FEATURE_SETTINGS_HUB),
 * `/settings` is a page of links and every section opens on a page of its
 * own: the child pages below (pricing, texting & calls, templates, team,
 * account, …) where a section has one, `/settings/<section>`
 * (pages/settings-leaf.tsx) for the rest. Every `#settings-<id>` link ever
 * minted, and a return that only carries its card's query
 * (`?billing=subscribed`), is sent on to that page.
 *
 * Otherwise it is the one long page below.
 */
export default function SettingsPage() {
  const { company } = useSession();
  const { hash, search } = useLocation();
  if (company?.settingsHub === true) {
    const target = settingsHubRedirect(hash, search);
    if (target) return <Navigate to={target} replace />;
    return <SettingsHub />;
  }
  return <SettingsLongPage />;
}

function SettingsLongPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('settings.documentTitle'));
  const { company, user, can, refresh, publicDemo } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const goToRoutedSection = useGoToSettingsSection();

  const changePassword = useMutation({
    mutationFn: (payload: { currentPassword?: string; newPassword: string }) => authApi.changePassword(payload),
    // An account that had no password now has one, and the connected-accounts
    // section keys its "you cannot remove your last way in" rule off that.
    onSuccess: () => void refresh(),
  });

  // Job chat's nav entry asks the same question as its page and its card.
  const jobChat = useJobChatSettingsAccess();
  // Refer & Earn renders nothing while the program is switched off on this
  // deployment. Same key as the section's own read, so this costs no request.
  const referrals = useQuery({
    queryKey: ['referrals'],
    queryFn: () => referralsApi.overview(),
    enabled: company !== null && !publicDemo,
  });

  // `#settings-<id>` (and the mailbox return's `#integrations`) jumps to that
  // section once the cards exist, and again when the hash changes while this
  // page stays open — the trial banner, Back, and Forward. Job chat and Refer
  // & Earn only exist once their queries answer, so a cold load of their hash
  // waits for those too.
  useSettingsHashJump(company !== null && !jobChat.loading && !referrals.isLoading);

  // `#settings-pricing`, `#settings-numbering`, `#settings-messaging` and
  // `#settings-jobChat` name sections that are pages now. Replace rather
  // than push, so Back skips the old address. The
  // Twilio Connect callback still returns to `#settings-messaging` with a
  // `?twilioConnect=` flag that only the texting card words for a person, so
  // that return goes straight to the texting page.
  const routedSection =
    location.hash === '#settings-messaging' && new URLSearchParams(location.search).has('twilioConnect')
      ? settingsSectionRoute('texting')
      : settingsRouteForHash(location.hash);
  if (routedSection) return <Navigate to={`${routedSection}${location.search}`} replace />;

  if (!company) return <LoadingBlock label={t('settings.loadingSettings')} />;

  const hubRoutes = settingsHubRoutesOn(company);
  const hubHashRoute = hubRoutes ? SETTINGS_HUB_HASH_ROUTES[location.hash] : undefined;
  // The query string rides along: the mailbox OAuth return reports its
  // outcome there, and the page it lands on is the one that reads it.
  if (hubHashRoute) return <Navigate to={`${hubHashRoute}${location.search}`} replace />;
  const hubSectionRoute = (id: string) => (hubRoutes ? SETTINGS_HUB_SECTION_ROUTES[id] : undefined);
  /** The setup guide's and tiles' "go there": the section's page, or its card. */
  const goToSection = (id: string) => {
    const to = hubSectionRoute(id);
    if (to) navigate(to);
    else goToRoutedSection(id);
  };

  // The nav mirrors exactly what renders below: every section that a
  // permission hides here is also absent from the rail.
  //
  // A section whose whole subject the PLAN does not carry is hidden the same
  // way — a company on Free has no visit reminders to configure and no
  // invoice tips to set. Billing is never hidden: it is where the plan can be
  // changed, and hiding the way up would be the one gate with no way through.
  // The API and texting cards stay visible on purpose; each renders its own
  // upgrade note, which is the older pattern and the right one for a card
  // that has something to say to a company that cannot use it yet.
  const has = (feature: PlanFeature) => companyHasPlanFeature(company, feature);
  const showJobChat = jobChat.available;
  const showReferrals = Boolean(publicDemo) || referrals.data?.enabled === true;
  const sectionDefs: Array<{ group: string; id: string; when: boolean }> = [
    { group: 'business', id: 'company', when: true },
    { group: 'business', id: 'pricing', when: true },
    { group: 'business', id: 'numbering', when: true },
    { group: 'business', id: 'library', when: can('templates:read') },
    // With child pages on, Room types and Layouts are tabs of the Templates
    // page that the library entry opens, so they are not entries of their own.
    { group: 'business', id: 'roomTypes', when: can('templates:read') && !hubRoutes },
    { group: 'business', id: 'layouts', when: can('templates:read') && !hubRoutes },
    // The checklist library is a page of its own; this is the way in. Same
    // gates as the page and its list endpoint, so the link never opens an error.
    { group: 'business', id: 'checklists', when: has('scheduling') && can('customers:read') },
    { group: 'business', id: 'locations', when: can('company:read') },
    { group: 'scheduling', id: 'hours', when: has('scheduling') },
    { group: 'scheduling', id: 'recurringHorizon', when: has('scheduling') && can('company:write') },
    { group: 'scheduling', id: 'calendar', when: can('schedule:read') && has('scheduling') },
    { group: 'communication', id: 'reminders', when: has('scheduling') },
    { group: 'communication', id: 'lateCancel', when: has('scheduling') },
    { group: 'communication', id: 'fillSlots', when: can('company:read') },
    { group: 'communication', id: 'followups', when: true },
    { group: 'communication', id: 'reengagement', when: user?.role === 'owner' && can('company:write') },
    { group: 'communication', id: 'notifications', when: true },
    // Texting setup and calls are pages of their own now (/settings/texting,
    // /settings/calls), behind the gate the one messaging section had. Calls
    // only where this installation answers calls: elsewhere its cards render
    // nothing.
    { group: 'communication', id: 'texting', when: can('company:write') },
    { group: 'communication', id: 'calls', when: can('company:write') && company.voiceInboxAvailable === true },
    { group: 'communication', id: 'jobChat', when: showJobChat },
    { group: 'communication', id: 'ai', when: can('company:read') },
    { group: 'website', id: 'widget', when: can('company:write') && has('online_intake') },
    // The photo-quote upload page rides the same request link, so it sits
    // beside the form's own settings. Behind the deployment flag as well.
    {
      group: 'website',
      id: 'photoQuotes',
      when: company.photoQuotesEnabled && can('company:read') && has('online_intake'),
    },
    // Every plan: Free takes card payment links for one job at a time
    // (`single_job`), so its Stripe account is set up here too.
    { group: 'money', id: 'payments', when: can('payments:read') && has('single_job') },
    // Every plan: the types a payment on a quote is recorded as (Cash, Check,
    // Zelle…) are Free's too.
    { group: 'money', id: 'paymentMethods', when: can('payments:read') && has('quote_payments') },
    { group: 'money', id: 'tips', when: has('invoicing') },
    { group: 'money', id: 'billing', when: true },
    { group: 'access', id: 'team', when: can('users:manage') },
    // Pay and job profit (0035): the owner edits, managers read, crew never.
    { group: 'access', id: 'teamPay', when: can('reports:read') && has('job_profit') },
    // ChatGPT & Claude: everybody connects their own, and it works as them —
    // limited to what their role allows — so everybody gets the section.
    { group: 'access', id: 'aiAssistants', when: true },
    { group: 'access', id: 'apiKeys', when: can('api_keys:manage') },
    { group: 'access', id: 'apiDocs', when: can('api_keys:manage') },
    { group: 'account', id: 'account', when: true },
    // Beside the account's other settings rather than under "access": these are
    // the signed-in person's own protections, not the company's roster.
    { group: 'account', id: 'security', when: true },
    { group: 'account', id: 'referrals', when: showReferrals },
    { group: 'account', id: 'contact', when: true },
    // With the Work hub on, Logs left the sidebar for the hub; this is its
    // second way in, beside the data export.
    { group: 'account', id: 'logs', when: company.navSecondaryHub === true && can('logs:read') },
    { group: 'account', id: 'data', when: can('company:export') },
    { group: 'account', id: 'delete', when: true },
  ];

  const primaryIds = [
    'company',
    'pricing',
    'library',
    'roomTypes',
    'layouts',
    'checklists',
    'hours',
    'recurringHorizon',
    'payments',
    'paymentMethods',
  ];
  const everydayIds = ['notifications', 'billing', 'team', 'account', 'security', 'contact'];
  const order = [...primaryIds, ...everydayIds];
  sectionDefs.sort((a, b) => {
    const rank = (id: string) => (order.includes(id) ? order.indexOf(id) : order.length);
    return rank(a.id) - rank(b.id);
  });
  const navGroups: SettingsNavGroup[] = [];
  for (const def of sectionDefs) {
    if (!def.when) continue;
    const groupId = primaryIds.includes(def.id) ? 'essentials' : everydayIds.includes(def.id) ? 'everyday' : 'advanced';
    let group = navGroups.find((candidate) => candidate.id === groupId);
    if (!group) {
      group = { id: groupId, label: t(`settings.nav.groups.${groupId}`), sections: [] };
      navGroups.push(group);
    }
    // The library entry opens all three template managers on the hub, so it
    // carries the hub's name and all three cards' search words.
    const copyId = hubRoutes && def.id === 'library' ? 'templates' : def.id;
    group.sections.push({
      id: def.id,
      label: t(`settings.nav.sections.${copyId}`),
      keywords: t(`settings.nav.keywords.${copyId}`),
      icon: <SettingsNavIcon name={def.id} />,
      to: hubSectionRoute(def.id),
    });
  }

  return (
    <>
      <PageHeader title={t('settings.title')} description={t('settings.description')} />

      <div className="md:grid md:grid-cols-[230px_minmax(0,1fr)] md:items-start md:gap-8">
        <SettingsNav groups={navGroups} />

        <div>
          <h2 className="mb-2 text-xl font-semibold">{t('settings.essentialsHeading')}</h2>
          <p className="mb-6 text-sm text-[color:var(--color-muted)]">{t('settings.essentialsHint')}</p>
          <div className="mb-6 grid grid-cols-2 gap-2">
            {navGroups[0]?.sections
              .filter((section) => section.id !== 'company' && section.id !== 'paymentMethods')
              .map((section) => {
                const route = section.to ?? settingsSectionRoute(section.id);
                return route ? (
                  <Link key={section.id} to={route} className="btn btn-secondary text-left">
                    {section.label}
                  </Link>
                ) : (
                  <button
                    key={section.id}
                    type="button"
                    className="btn btn-secondary text-left"
                    onClick={() => goToSection(section.id)}
                  >
                    {section.label}
                  </button>
                );
              })}
          </div>
          <SettingsAnchor id="company">
            <CompanyDetailsCard />
            <ExchangeRatesCard />
          </SettingsAnchor>

          {can('templates:read') && !hubRoutes ? (
            <SettingsAnchor id="library">
              <ServiceTemplatesSection />
            </SettingsAnchor>
          ) : null}

          {can('templates:read') && !hubRoutes ? (
            <SettingsAnchor id="roomTypes">
              <RoomTemplatesSection />
            </SettingsAnchor>
          ) : null}

          {can('templates:read') && !hubRoutes ? (
            <SettingsAnchor id="layouts">
              <GroupTemplatesSection />
            </SettingsAnchor>
          ) : null}

          {has('scheduling') && can('customers:read') ? (
            <SettingsAnchor id="checklists">
              <ChecklistLibraryCard />
            </SettingsAnchor>
          ) : null}

          {has('scheduling') ? (
            <SettingsAnchor id="hours">
              <WorkingHoursCard />
            </SettingsAnchor>
          ) : null}

          {has('scheduling') && can('company:write') ? (
            <SettingsAnchor id="recurringHorizon">
              <RecurringHorizonCard key={company.id} />
            </SettingsAnchor>
          ) : null}

          {can('payments:read') && has('single_job') ? (
            <SettingsAnchor id="payments">
              <PaymentSettingsSection chargesCompletedVisits={has('invoicing')} />
            </SettingsAnchor>
          ) : null}

          {can('payments:read') && has('quote_payments') ? (
            <SettingsAnchor id="paymentMethods">
              <PaymentMethodsSection />
            </SettingsAnchor>
          ) : null}
          <SetupGuide onJump={goToSection} />
          <h2 className="mb-4 text-xl font-semibold">{t('settings.everydayHeading')}</h2>
          <SettingsAnchor id="notifications">
            <TomorrowDigestCard />
            <NotificationsCard />
          </SettingsAnchor>

          <SettingsAnchor id="billing">
            {publicDemo ? (
              <DemoLimitedSection heading={t('billing.heading')} note={t('publicDemo.billingNote')} />
            ) : (
              <BillingSection />
            )}
          </SettingsAnchor>

          {can('users:manage') && !hubRoutes ? (
            <SettingsAnchor id="team">
              <TeamSection />
              <SavedTeamsSection />
              {has('staff_portal') ? <ShiftSwapPolicyCard /> : null}
            </SettingsAnchor>
          ) : null}

          {hubRoutes ? null : (
            <SettingsAnchor id="account">
              <ProfileSection />

              <PasswordSection
                hasPassword={user?.hasPassword ?? true}
                onSubmit={(payload) => changePassword.mutateAsync(payload)}
                busy={changePassword.isPending}
              />

              <ConnectedAccountsSection />

              {/* Beside the sign-in methods rather than merged with them, because
                they answer different questions: that list is how this person
                proves who they are, and this one is whose mailbox and calendar
                the app may act through. The same Google account is often in
                both, and often is not. */}
              <div id="integrations">
                {publicDemo ? (
                  <DemoLimitedSection
                    heading={t('publicDemo.integrationsHeading')}
                    note={t('publicDemo.integrationsNote')}
                  />
                ) : (
                  <IntegrationsSection />
                )}
              </div>
            </SettingsAnchor>
          )}

          {hubRoutes ? null : (
            <SettingsAnchor id="security">
              <SecuritySection />
            </SettingsAnchor>
          )}

          <SettingsAnchor id="contact">
            <section className="card mb-6" aria-labelledby="contact-heading">
              <h2 id="contact-heading" className="mb-3 text-lg font-semibold">
                {t('settings.contactHeading')}
              </h2>
              <FounderContactLine />
            </section>
          </SettingsAnchor>
          <details className="mb-6" data-settings-advanced>
            <summary className="cursor-pointer rounded-md border border-[color:var(--color-line)] p-4 focus-visible:outline-2 focus-visible:outline-offset-2">
              <span className="font-semibold">{t('settings.advancedHeading')}</span>
              <span className="mt-1 block text-sm text-[color:var(--color-muted)]">
                {t('settings.advancedHint', { pricing: t('settings.nav.sections.pricing') })}
              </span>
            </summary>
            <div className="pt-6">
              {can('company:read') ? (
                <SettingsAnchor id="locations">
                  <LocationsSection />
                </SettingsAnchor>
              ) : null}

              {can('schedule:read') && has('scheduling') ? (
                <SettingsAnchor id="calendar">
                  {publicDemo ? (
                    <DemoLimitedSection heading={t('feeds.heading')} note={t('publicDemo.feedsNote')} />
                  ) : (
                    <CalendarFeedsSection />
                  )}
                  <CalendarColorsSettings />
                </SettingsAnchor>
              ) : null}

              {has('scheduling') ? (
                <SettingsAnchor id="reminders">
                  <RemindersCard />
                  <ChecklistCompletionCard />
                </SettingsAnchor>
              ) : null}

              {has('scheduling') ? (
                <SettingsAnchor id="lateCancel">
                  <LateCancelCard />
                </SettingsAnchor>
              ) : null}

              {can('company:read') ? (
                <SettingsAnchor id="fillSlots">
                  <FillSlotsCard />
                </SettingsAnchor>
              ) : null}

              <SettingsAnchor id="followups">
                <FollowUpsCard />
              </SettingsAnchor>

              {user?.role === 'owner' && can('company:write') ? (
                <SettingsAnchor id="reengagement">
                  <ReengagementCard />
                </SettingsAnchor>
              ) : null}

              {can('company:read') ? (
                <SettingsAnchor id="ai">
                  <AiSection />
                </SettingsAnchor>
              ) : null}

              {can('company:write') && has('online_intake') ? (
                <SettingsAnchor id="widget">
                  <WidgetSection />
                </SettingsAnchor>
              ) : null}

              {company.photoQuotesEnabled && can('company:read') && has('online_intake') ? (
                <SettingsAnchor id="photoQuotes">
                  <PhotoQuotesCard />
                </SettingsAnchor>
              ) : null}

              {has('invoicing') ? (
                <SettingsAnchor id="tips">
                  <TipsCard />
                </SettingsAnchor>
              ) : null}

              {can('reports:read') && has('job_profit') && !hubRoutes ? (
                <SettingsAnchor id="teamPay">
                  <TeamPaySection />
                  <MarginFloorSection />
                </SettingsAnchor>
              ) : null}

              <SettingsAnchor id="aiAssistants">
                <AiAssistantsSection />
              </SettingsAnchor>

              {can('api_keys:manage') ? (
                <SettingsAnchor id="apiKeys">
                  <ApiKeysSection />
                </SettingsAnchor>
              ) : null}

              {can('api_keys:manage') ? (
                <SettingsAnchor id="apiDocs">
                  <ApiDocsSection />
                </SettingsAnchor>
              ) : null}

              {showReferrals ? (
                <SettingsAnchor id="referrals">
                  {publicDemo ? (
                    <DemoLimitedSection heading={t('referrals.title')} note={t('publicDemo.referralsNote')} />
                  ) : (
                    <ReferralsSection />
                  )}
                </SettingsAnchor>
              ) : null}

              {company.navSecondaryHub === true && can('logs:read') ? (
                <SettingsAnchor id="logs">
                  <section className="card mb-6" aria-labelledby="logs-heading">
                    <h2 id="logs-heading" className="mb-3 text-lg font-semibold">
                      {t('settings.logsHeading')}
                    </h2>
                    <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('settings.logsDescription')}</p>
                    <Link className="btn btn-secondary" to="/logs">
                      {t('settings.openLogs')}
                    </Link>
                  </section>
                </SettingsAnchor>
              ) : null}

              {can('company:export') ? (
                <SettingsAnchor id="data">
                  <section className="card mb-6" aria-labelledby="export-heading">
                    <h2 id="export-heading" className="mb-3 text-lg font-semibold">
                      {t('settings.exportHeading')}
                    </h2>
                    <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('settings.exportDescription')}</p>
                    <a className="btn btn-secondary" href="/api/v1/company/export">
                      {t('settings.downloadExport')}
                    </a>
                  </section>
                </SettingsAnchor>
              ) : null}

              <SettingsAnchor id="delete">
                <DeleteAccountSection />
              </SettingsAnchor>
            </div>
          </details>
        </div>
      </div>
    </>
  );
}

/**
 * Change your password — or set one for the first time.
 *
 * An account created through a social provider has no current password to
 * confirm, so the field is not shown and not sent. Rendering it disabled, or
 * asking for something that does not exist, would just be a dead end on the one
 * page that is supposed to get them out of it.
 */
export function PasswordSection({
  hasPassword,
  onSubmit,
  busy,
}: {
  hasPassword: boolean;
  onSubmit: (payload: { currentPassword?: string; newPassword: string }) => Promise<unknown>;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [result, setResult] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setResult(null);
    try {
      await onSubmit({ ...(hasPassword ? { currentPassword } : {}), newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setResult({
        tone: 'success',
        text: hasPassword ? t('settings.passwordChanged') : t('settings.passwordSet'),
      });
    } catch (caught) {
      setResult({
        tone: 'error',
        text:
          caught instanceof ApiRequestError
            ? caught.message
            : hasPassword
              ? t('settings.passwordChangeError')
              : t('settings.passwordSetError'),
      });
    }
  }

  return (
    <section className="card mb-6" aria-labelledby="password-heading">
      <h2 id="password-heading" className="mb-4 text-lg font-semibold">
        {hasPassword ? t('settings.passwordHeading') : t('settings.passwordHeadingSet')}
      </h2>

      {hasPassword ? null : (
        <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('settings.setPasswordDescription')}</p>
      )}

      {result ? (
        <div className="mb-4">
          <Alert tone={result.tone}>{result.text}</Alert>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} noValidate>
        {hasPassword ? (
          <TextInput
            label={t('settings.currentPassword')}
            type="password"
            autoComplete="current-password"
            required
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
          />
        ) : null}
        <TextInput
          label={hasPassword ? t('settings.newPassword') : t('settings.passwordFieldGeneric')}
          type="password"
          autoComplete="new-password"
          required
          minLength={12}
          hint={t('settings.newPasswordHint')}
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
        />
        <Button type="submit" variant="primary" busy={busy}>
          {hasPassword ? t('settings.changePassword') : t('settings.setPasswordButton')}
        </Button>
      </form>
    </section>
  );
}
